import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { InvestorMonthlyMetric, CohortRevenueRow } from './investorCohortApi';

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  SVGRenderer,
]);

type ChartProps = {
  monthly: InvestorMonthlyMetric[];
};

const useChart = (
  ref: React.RefObject<HTMLDivElement>,
  option: echarts.EChartsCoreOption
) => {
  React.useEffect(() => {
    if (!ref.current) return undefined;
    const chart = echarts.init(ref.current);
    chart.setOption(option);
    const resize = () => chart.resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
  }, [ref, option]);
};

export const OperatingTrendChart = ({ monthly }: ChartProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const option = React.useMemo<echarts.EChartsCoreOption>(() => ({
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0 },
    grid: { left: 55, right: 65, top: 25, bottom: 70 },
    xAxis: {
      type: 'category',
      data: monthly.map((row) => `${row.month}${row.is_partial_month ? ' MTD' : ''}`),
      axisLabel: { rotate: 40 },
    },
    yAxis: [
      { type: 'value', name: 'Customers' },
      {
        type: 'value',
        name: 'GMV (₹ lakh)',
        axisLabel: { formatter: (value: number) => `${value}` },
      },
    ],
    series: [
      {
        name: 'New customers',
        type: 'bar',
        data: monthly.map((row) => row.new_customers),
      },
      {
        name: 'Paying buyers',
        type: 'line',
        smooth: true,
        data: monthly.map((row) => row.paying_customers),
      },
      {
        name: 'Repeat paying buyers',
        type: 'line',
        smooth: true,
        data: monthly.map((row) => row.repeat_paying_buyers),
      },
      {
        name: 'GMV',
        type: 'line',
        yAxisIndex: 1,
        data: monthly.map((row) => Number((row.gmv_inr / 100000).toFixed(2))),
      },
    ],
  }), [monthly]);
  useChart(ref, option);
  return <div ref={ref} style={{ width: '100%', height: 390 }} />;
};

export const CohortRevenueChart = ({ revenue }: { revenue: CohortRevenueRow[] }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const totals = React.useMemo(() => {
    const byCohort = new Map<string, number>();
    revenue.forEach((row) => {
      byCohort.set(
        row.cohort_month,
        Math.max(byCohort.get(row.cohort_month) || 0, row.cumulative_gmv_inr)
      );
    });
    return Array.from(byCohort.entries());
  }, [revenue]);
  const option = React.useMemo<echarts.EChartsCoreOption>(() => ({
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value: unknown) => `₹${Number(value).toLocaleString('en-IN')} lakh`,
    },
    grid: { left: 65, right: 25, top: 25, bottom: 65 },
    xAxis: {
      type: 'category',
      data: totals.map(([cohort]) => cohort),
      axisLabel: { rotate: 40 },
    },
    yAxis: { type: 'value', name: 'Cumulative GMV (₹ lakh)' },
    series: [{
      name: 'Cumulative cohort GMV',
      type: 'bar',
      data: totals.map(([, value]) => Number((value / 100000).toFixed(2))),
    }],
  }), [totals]);
  useChart(ref, option);
  return <div ref={ref} style={{ width: '100%', height: 350 }} />;
};
